import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";

const weglotToken = import.meta.env.VITE_WEGLOT_TOKEN;

interface WeglotContextProps {
  currLanguage?: string;
  setCurrLanguage: (value: string) => void;
  isWeglotReady: boolean;
  switchLanguage: (language: string) => void;
}

const WeglotContext = createContext<WeglotContextProps>(undefined!);

export const useWeglot = (): WeglotContextProps => {
  const context = useContext(WeglotContext);
  if (!context) throw new Error("WeglotContext must be used inside WeglotProvider");
  return context;
};

export const WeglotProvider = ({children}: PropsWithChildren) => {
  const {search} = useLocation();
  const queryParams = new URLSearchParams(search);
  const [currLanguage, setCurrLanguage] = useState<string>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isWeglotReady, setIsWeglotReady] = useState(false);
  const urlLanguage = queryParams.get("language");

  const storedLanguage = useMemo(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("wglang") || "en";
    }
    return "en";
  }, []);

  const defaultLanguage = useMemo(() => urlLanguage || storedLanguage, [urlLanguage, storedLanguage]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Weglot?.initialize) {
      window.Weglot.initialize({
        api_key: weglotToken,
        originalLanguage: "en",
        destinationLanguages: ["es"],
        defaultLanguage,
        cache: true,
      })?.catch(() => {});
      window.Weglot.on?.("initialized", () => setIsWeglotReady(true));
      if (urlLanguage) {
        localStorage.setItem("wglang", defaultLanguage);
      }
    }

    setCurrLanguage(defaultLanguage);
    setIsInitialized(true);
  }, [defaultLanguage, urlLanguage]);

  const switchLanguage = (language: string) => {
    if (window.Weglot?.switchTo) {
      window.Weglot.switchTo(language);
    }
    setCurrLanguage(language);
  };

  const contextValue = useMemo(
    () => ({currLanguage, setCurrLanguage, isWeglotReady, switchLanguage}),
    [currLanguage, isWeglotReady]
  );

  if (!isInitialized) return null;

  return <WeglotContext.Provider value={contextValue}>{children}</WeglotContext.Provider>;
};
